import { useEffect, useState, useCallback, useContext } from 'react'
import { Box, Grid, Stack, Skeleton, Typography, Paper, Button } from '@mui/material'
import { ChevronLeft } from '@mui/icons-material'
import Page from '../layout/Page'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { getThumbnailImage, getArticleById, getDriveItemByFQDN } from './data'
import './Article.css'
import CacheContext from '../cache/CacheContext'
import { renderSpImageWebPart, imageExtensionsRegex, addDomainsToPossiblyRelativeUrls } from './utils'
import ResourcePreviewPopover from './ResourceLibrary/ResourcePreviewPopover'
import ProfileAvatarCard from './ArticleComponents/ProfileAvatarCard'
import { getSiteConfig } from '../community/siteProvider'
import NoImage from '../img/NoImage.png'

export default function ArticleDetailPage(props = {}) {
  const location = useLocation()
  const cacheContext = useContext(CacheContext)
  const navigate = useNavigate()
  const params = useParams()
  const { articleId } = params

  const {
    breadcrumbPrefix
  } = props

  const [isLoading, setIsLoading] = useState(false)
  const [article, setArticle] = useState({})
  const [bannerSection, setBannerSection] = useState()
  const [bannerImage, setBannerImage] = useState()
  const [bannerVideoPreview, setBannerVideoPreview] = useState()
  const [sections, setSections] = useState()
  const [author, setAuthor] = useState(null)
  const isBlobUrl = useCallback((url) => typeof url === 'string' && /^\s*blob:/i.test(url), [])

  const mapWithConcurrency = useCallback(async (arr, mapper, limit = 4) => {
    if (!Array.isArray(arr) || !arr.length) return []
    const results = new Array(arr.length)
    let index = 0

    const workers = Array.from({ length: Math.min(limit, arr.length) }, async () => {
      while (true) {
        const current = index
        index += 1
        if (current >= arr.length) return
        results[current] = await mapper(arr[current], current)
      }
    })

    await Promise.all(workers)
    return results
  }, [])

  const elemRef = useCallback((node) => {
    if (node !== null) {
      const images = node.querySelectorAll([".imagePlugin"])
      images.forEach(image => {
        const url = image.getAttribute('data-imageurl')
        const width = image.getAttribute('data-width')
        const height = image.getAttribute('data-height')
        const alignment = image.getAttribute('data-alignment')
        const caption = image.getAttribute('data-captiontext')
        const figure = document.createElement('figure')
        image.parentNode.insertBefore(figure, image)

        const img = document.createElement('img')
        img.setAttribute('src', isBlobUrl(url) ? NoImage : url)
        img.style.width = width + 'px'
        img.style.height = height + 'px'

        figure.appendChild(img)
        figure.className = image.className
        figure.style.width = width + 'px'
        figure.style.height = height + 'px'

        image.remove()

        const figcaption = document.createElement('figcaption')
        const figcaptionspan = document.createElement('span')
        figcaptionspan.innerHTML = caption
        figcaption.appendChild(figcaptionspan)
        figure.appendChild(figcaption)
      })
    }
  }, [isBlobUrl])


  const rewriteSharePointUrls = useCallback(async (_html, transform) => {
    if (typeof _html !== 'string') throw new TypeError('html must be a string');
    if (typeof transform !== 'function')
      throw new TypeError('transform must be a function');

    const html = addDomainsToPossiblyRelativeUrls(_html)
    const ATTR_RE =
      /(\s*[\w:-]+\s*=\s*['"])(https:\/\/groups.coca-cola.com\/(?:sites|_layouts)\/(?:[^'"])*[^'"]+)(['"])/gi;

    let out = '';
    let lastPos = 0;
    let match;

    while ((match = ATTR_RE.exec(html)) !== null) {

      const [whole, prefix, url, quote] = match;
      // Copy text before this attribute
      out += html.slice(lastPos, match.index);

      // Run (or await) the user's transform
      const newUrl = await transform(url);

      // Re-insert the attribute with the rewritten URL
      out += `${prefix}${newUrl}${quote}`;

      lastPos = match.index + whole.length;
    }

    // Append any trailing text after the final match
    out += html.slice(lastPos);
    return out;
  }, [])

  const replaceImageUrls = useCallback(async (html) => {
    const htmlReplaced = addDomainsToPossiblyRelativeUrls(html)

    const rewritten = await rewriteSharePointUrls(htmlReplaced, async (imgSrc) => {
      if (isBlobUrl(imgSrc)) {
        return NoImage
      }
      if (imageExtensionsRegex.test(imgSrc)) {
        const img = await getThumbnailImage({ thumbnailWebUrl: imgSrc }, cacheContext)
        return isBlobUrl(img) ? NoImage : img
      } else {
        return imgSrc
      }
    })
    return rewritten
  }, [cacheContext, rewriteSharePointUrls, isBlobUrl])

  // File and Medie web part type: b7dd04e1-19ce-4b24-9132-b60a1c2b910d
  // Image web part type: d1d91016-032f-456d-98a4-721247c305e8

  const renderWebPart = useCallback(async (webpart) => {
    if (webpart['@odata.type'] === '#microsoft.graph.textWebPart') {

      const updatedHtml = await replaceImageUrls(webpart?.innerHtml)
      return <span key={webpart.id} className='webpart' ref={elemRef} dangerouslySetInnerHTML={{
        __html: updatedHtml || ''
      }} />
    }

    if (webpart['@odata.type'] === '#microsoft.graph.standardWebPart') {

      // Image Web Part
      if (webpart.webPartType === 'd1d91016-032f-456d-98a4-721247c305e8') {
        const html = await renderSpImageWebPart(webpart, cacheContext) || ''
        return <span key={webpart.id} className='webpart' dangerouslySetInnerHTML={{
          __html: html
        }} />
      }

      // File & Media
      if (webpart.webPartType === 'b7dd04e1-19ce-4b24-9132-b60a1c2b910d') {
        if(!webpart?.data?.properties?.file) return ''
        const driveItem = await getDriveItemByFQDN(webpart.data.properties.file)
        console.log('driveItem', driveItem)
        const thumbnail = isBlobUrl(driveItem?.thumbnails?.[0]?.large?.url)
          ? NoImage
          : driveItem?.thumbnails?.[0]?.large?.url
        const fileType = driveItem?.file?.mimeType?.split('/')?.[1] || ''
        const texts = webpart?.data?.serverProcessedContent?.searchablePlainTexts || []
        const title = texts.find(f => f.key === 'title')?.value
        console.log('article title', article.title)
        return <Box key={webpart.id} sx={{
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'column',
          alignContent: 'center',
          alignItems: 'center',
        }}>
          <ResourcePreviewPopover
            file={{
              fields: {
                LinkFilename: driveItem.name,
                Title: article.title
              }
            }}
            resourceId={driveItem.id}
            siteId={driveItem.parentReference.siteId}
            driveId={driveItem.parentReference.driveId}
            icon={<Box sx={{
              transition: 'transform 200ms ease, background-color 200ms ease',
              transformOrigin: 'center',
              ':hover': {
                transform: 'scale(1.01, 1.01);',
                filter: 'brightness(0.95)'
              }
            }}>
              <img alt="asdf" src={thumbnail} style={{width: '100%',height: '100%'}}/>
            </Box>}
          />

        </Box>
      }
    }
  }, [article.title, cacheContext, elemRef, replaceImageUrls, isBlobUrl])

  useEffect(() => {
    let active = true

    ;(async () => {
      setIsLoading(true)
      const _article = await getArticleById(articleId)
      const { canvasLayout } = _article
      const _bannerSection = canvasLayout.horizontalSections.find(section => {
        return section?.columns?.some(col => col.webparts.some(wp => wp.data.title === 'Banner'))
      })

      const bannerSectionIndex = canvasLayout.horizontalSections.indexOf(_bannerSection)
      const __bannerSection = canvasLayout.horizontalSections.splice(bannerSectionIndex, 1)[0]
      const bannerSectionData = __bannerSection?.columns?.[0]?.webparts?.[0]?.data
      const imgsrc = bannerSectionData?.serverProcessedContent?.imageSources?.find(o => o.key === 'imageSource')?.value || _article.thumbnailWebUrl || _article.bannerImageWebUrl
      const bannerProperties = bannerSectionData?.properties
      if(imgsrc) {
        if (imageExtensionsRegex.test(imgsrc)) {
          const replacedImg = await replaceImageUrls(imgsrc)
          const _bannerImage = await getThumbnailImage({ thumbnailWebUrl: replacedImg }, cacheContext)
          if (active) setBannerImage(isBlobUrl(_bannerImage) ? NoImage : _bannerImage)
        } else if (/\\.mp4/i.test(imgsrc)) {

        }
      }
      if (bannerProperties?.authors) {
        if (active) setAuthor(bannerProperties.authors[0] ?? null)
      }

      if (!active) return
      setBannerSection(__bannerSection)
      setArticle(_article)
      setIsLoading(false)
    })()

    return () => {
      active = false
    }
  }, [articleId, cacheContext, replaceImageUrls])

  const goBack = () => {
    if (location.key === 'default') {
      navigate(`/${getSiteConfig().sitePath}/news`)
    } else {
      navigate(-1)
    }
  }

  useEffect(() => {
    if (!article?.canvasLayout?.horizontalSections?.length) return

    let active = true
    ;(async () => {
      const _sections = await mapWithConcurrency(
        article?.canvasLayout?.horizontalSections,
        async (horizontalSection) => {
          const columns = await mapWithConcurrency(
            horizontalSection?.columns || [],
            async (column) => {
              const webparts = await mapWithConcurrency(
                column?.webparts || [],
                renderWebPart,
                3,
              )

              return (
                <Grid
                  key={`col_${column.id}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  item
                  xs={11.8}
                  lg={column.width - 0.1}
                  className={`column`}
                >
                  {webparts}
                </Grid>
              )
            },
            2,
          )

          return (
            <Grid
              key={`section_${horizontalSection.id}`}
              container
              className={`horizontalSection ${horizontalSection.layout} ${horizontalSection.emphasis}Emphasis`}
            >
              {columns}
            </Grid>
          )
        },
        2,
      )

      if (active) setSections(_sections)
    })()

    return () => {
      active = false
    }
  }, [article, renderWebPart, mapWithConcurrency])

  const breadcrumb = <div><span><Button onClick={goBack}><ChevronLeft />Go Back</Button>  </span></div>
  return (
    <Page {...props} breadcrumb={breadcrumb} isLoading={false}>

      <Paper
        component="form"
        sx={{ backgroundColor: 'transparent', p: '0 0 3em 0', border: 'none', boxShadow: 'none', display: 'flex', flexDirection: 'column', flexWrap: 'wrap', margin: '1em auto' }}
      >

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 380,
            overflow: 'hidden',
          }}
        >
          {bannerImage && <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${bannerImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(5px)',
              transform: 'scale(1.1)',
            }}
          />}

          {bannerImage && <Box
            component="img"
            src={bannerImage}
            alt="Banner"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              height: '100%',
              width: 'auto',
              objectFit: 'contain',
              zIndex: 1,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          />}

          {/* {bannerVideoPreview && <Box sx={{ flexGrow: 1, height: '600px' }}>
            <iframe
              src={bannerVideoPreview.getUrl}
              title="File Preview"
              style={{ width: '100%', border: 'none' }}
            />
          </Box>} */}

          {isLoading && (
            <Skeleton
              animation="wave"
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 3,
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            padding: '0 1.5%',
            width: '100%',
            bgcolor: 'white',
            paddingBottom: '10px'
          }}
        >
          {!isLoading && <Stack rowGap={.5} padding='1%'>
            <Typography variant="h4">{article?.title}</Typography>
            <ProfileAvatarCard 
              profileEmail={author ? author.email : article?.createdBy?.user?.email}
              profileName={author ? author.name : article?.createdBy?.user?.displayName} 
              profileJobTitle={ author?.role ?? undefined}
              />
          </Stack>}
        </Box>

        {(isLoading || !sections) && <Stack
          gap={2}
          sx={{
            margin: '20px',
            width: 'calc(94%)',
            marginLeft: 'calc(3%)',
            height: '60px',
          }}>
          <Skeleton width='100%' />
          <Stack gap={2} direction='row'
            sx={{
              width: '100%',
              height: '200px',
            }}>
            <Skeleton sx={{ flexGrow: 2 }} />
            <Skeleton sx={{ flexGrow: 1 }} />
          </Stack>
          <Stack gap={2} direction='row'
            sx={{
              width: '100%',
              height: '200px',
            }}>
            <Skeleton sx={{ flexGrow: 1 }} />
            <Skeleton sx={{ flexGrow: 2 }} />
          </Stack>
        </Stack>

        }

        {!isLoading && sections && <div style={{
          backgroundColor: 'white',
          width: '100%',
          height: '100%',
          padding: '0 2%',
        }} className='article-body'>
          {sections}
          {/* {article?.canvasLayout?.horizontalSections.map(horizontalSection => {
            return <Grid container className={`horizontalSection ${horizontalSection.layout}`}>
              {horizontalSection.columns.map(column => {
                return <Grid sx={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
                  item xs={column.width} className={`column`}>
                  {column.webparts.map(renderWebPart)}
                </Grid>
              })}
            </Grid>
          })} */}
        </div>}
      </Paper>
    </Page >
  )
}